import React from 'react'

import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'

import StopIcon from '@mui/icons-material/Stop'
import PauseIcon from '@mui/icons-material/Pause'
import ReplayIcon from '@mui/icons-material/Replay'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AddToQueueIcon from '@mui/icons-material/AddToQueue'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

import { RecSettingsTitle } from './VideoRecorder/RecSettingsTitle'
import {
    cancelScreenCapture,
    changeRecPanelMode,
    deleteQGRecording,
    pauseQGRecording,
    restartQGRecording,
    restartScreenCapture,
    resumeQGRecording,
    sendManualQGStep,
    stopQGRecordingNow,
    stopScreenCapture,
} from '../../services/swMessenger'
import { Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { QG_STATE, QG_STATE_UNION } from '../../types/quickGuidde'

type Props = {
    mode: 'quickGuidde' | 'playbook'
    qgState: QG_STATE_UNION | null
    closeModal: () => void
}
const getRecControls = (isQG: boolean, closeModal: () => void) => [
    {
        value: 'restart',
        label: 'Restart',
        icon: <ReplayIcon />,
        callback: isQG
            ? () => {
                  restartQGRecording()
              }
            : () => {
                  restartScreenCapture()
              },
    },
    {
        value: 'showPanel',
        label: 'Show panel',
        icon: <VisibilityIcon />,
        callback: () => {
            changeRecPanelMode({ mode: 'pageCircle' })
        },
    },
    ...(isQG
        ? [
              {
                  value: 'manualAction',
                  label: 'Screenshot',
                  icon: <AddToQueueIcon />,
                  callback: () => {
                      closeModal()
                      sendManualQGStep(true)
                  },
              },
          ]
        : []),
    {
        value: 'cancel',
        label: 'Delete',
        icon: <DeleteOutlineIcon />,
        callback: isQG
            ? () => {
                  deleteQGRecording()
              }
            : () => {
                  cancelScreenCapture()
              },
    },
]

const useStyles = makeStyles(theme => ({
    label: {
        display: 'flex',
        marginTop: theme.spacing(0.5),
        color: '#090C1099',
        fontSize: '12px',
        textAlign: 'center',
        '& svg': {
            height: theme.spacing(2),
            color: '#73CC6D',
        },
    },
    btn: {
        width: 'fit-content',
        backgroundColor: '#e3e3e3',
        '&:hover': {
            backgroundColor: '#dcdcdc',
        },
    },
    primaryBtn: {
        width: 'fit-content',
        color: '#ffffff',
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
}))

const RecPanelBtn = ({ primary = false, icon, label, onClick }) => {
    const classes = useStyles()

    return (
        <Box
            mx={1}
            mb={2}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
        >
            <IconButton
                className={primary ? classes.primaryBtn : classes.btn}
                onClick={onClick}
                size="large"
            >
                {icon}
            </IconButton>
            <Typography className={classes.label}>{label}</Typography>
        </Box>
    )
}

export const RecPanel = ({ mode, qgState, closeModal }: Props) => {
    const isQG: boolean = mode === 'quickGuidde'

    if (isQG && qgState === null) {
        return null
    }

    return (
        <>
            <Box pt={2}>
                <RecSettingsTitle />
            </Box>

            <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" px={5} py={2}>
                <RecPanelBtn
                    primary
                    label={
                        <>
                            <CheckCircleIcon color="inherit" /> Done
                        </>
                    }
                    icon={<StopIcon />}
                    onClick={
                        isQG
                            ? () => {
                                  stopQGRecordingNow(false)
                              }
                            : () => {
                                  stopScreenCapture({
                                      mxpMeta: {
                                          stopSource: 'iframeRecPanelStopBtn',
                                      },
                                  })
                              }
                    }
                />

                {isQG && (
                    <RecPanelBtn
                        label={qgState === QG_STATE.PAUSED ? 'Resume' : 'Pause'}
                        icon={
                            qgState === QG_STATE.PAUSED ? (
                                <PlayArrowIcon />
                            ) : (
                                <PauseIcon />
                            )
                        }
                        onClick={
                            qgState === QG_STATE.PAUSED
                                ? () => {
                                      resumeQGRecording()
                                  }
                                : () => {
                                      pauseQGRecording()
                                  }
                        }
                    />
                )}

                {getRecControls(isQG, closeModal).map(control => (
                    <RecPanelBtn
                        key={control.value}
                        icon={control.icon}
                        label={control.label}
                        onClick={control.callback}
                    />
                ))}
            </Box>
        </>
    )
}
